import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Имя query-параметра, в котором едет адрес возврата.
 */
const RETURN_TO_PARAM = 'returnTo';

/**
 * Куда разрешено возвращаться. Значение приходит из URL, то есть его может
 * подменить кто угодно, поэтому принимаем только внутренние пути личного
 * кабинета — иначе кнопка «Сохранить» станет средством увести пользователя
 * на чужой сайт.
 */
const isSafeReturnPath = (value: string | null): boolean => {
    if (!value) {
        return false;
    }

    // `//example.com` и `https://…` браузер считает внешним адресом
    return value.startsWith('/lk/') && !value.startsWith('//');
};

/**
 * Собирает `?returnTo=…` для перехода на страницу, с которой нужно вернуться
 * назад по кнопке «Сохранить» или «Отмена».
 *
 * @param path путь текущей страницы, например `/lk/worker/crm/3/orders/30`
 */
export const buildReturnToSearch = (path: string): string =>
    `?${RETURN_TO_PARAM}=${encodeURIComponent(path)}`;

/**
 * Возврат на страницу, с которой пришли.
 *
 * Адрес держим в query-параметре, а не в `location.state`: state теряется при
 * перезагрузке, а формы редактирования товара, услуги и задачи большие — уйти
 * на F5 посреди заполнения вполне реально. С параметром ссылку можно и просто
 * переслать.
 *
 * @example
 * const { returnTo, goBack } = useReturnTo();
 * // после сохранения
 * if (returnTo) goBack();
 * else navigate(`/lk/worker/crm/${crmId}/services/${id}`);
 */
export const useReturnTo = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const returnTo = useMemo(() => {
        const raw = searchParams.get(RETURN_TO_PARAM);
        return isSafeReturnPath(raw) ? raw : null;
    }, [searchParams]);

    /**
     * Уходит на адрес возврата. Если его нет, вызывает `fallback` — а когда и
     * его не передали, откатывается на шаг назад по истории.
     */
    const goBack = useCallback(
        (fallback?: () => void) => {
            if (returnTo) {
                navigate(returnTo);
                return;
            }

            if (fallback) {
                fallback();
                return;
            }

            navigate(-1);
        },
        [returnTo, navigate]
    );

    return { returnTo, goBack };
};
